/**
 * Chunk-load-failure escape hatch for the viewer's dynamic imports (issue
 * #130). Code-splitting moves Leaflet/the OSM minimap and the postprocessing
 * stack into lazily-fetched chunks; on a slow or captive network — or a
 * partial redeploy where the entry chunk loaded but a split chunk 404s — that
 * fetch can reject. `loadLazy` turns the rejection into a value the caller can
 * branch on so the viewer reports the degradation and keeps booting, rather
 * than letting an unhandled rejection white-screen it (hard constraint #7).
 */
export type LazyResult<T> = { ok: true; value: T } | { ok: false; error: Error };

/**
 * Runs a dynamic `import()` (or any module-returning thunk) and never throws:
 * resolves to `{ ok: true, value }` on success, or `{ ok: false, error }` on a
 * rejected import OR a synchronous throw from the importer. A non-Error
 * rejection is wrapped so callers can always read `error.message`.
 */
export async function loadLazy<T>(importer: () => Promise<T>): Promise<LazyResult<T>> {
  try {
    return { ok: true, value: await importer() };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err : new Error(String(err)) };
  }
}
